import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useActivityHeartbeat } from "@/lib/useActivityHeartbeat";
import { cachedLocalIP } from "@/lib/localIp";
import { describeDevice } from "@/lib/deviceInfo";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAdmin(data);
      } catch {
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Track engagement time whenever a dashboard user is signed in.
  useActivityHeartbeat(!!admin?.id);

  const login = async (email, password) => {
    const [local_ip, dev] = await Promise.all([cachedLocalIP(), describeDevice()]);
    const { data } = await api.post("/auth/login", {
      email, password, local_ip, device_family: dev.label,
    });
    // Spa Ops kiosk multi-session: each iPad gets its own session token so
    // admins can kick individual devices from the Connected Devices panel.
    // Stored per-browser; polled every 15 s by SpaOpsView; wiped on logout.
    if (data?.spa_ops_session_token) {
      try { localStorage.setItem("yacht_spa_ops_session_token", data.spa_ops_session_token); } catch { /* ignore */ }
    }
    setAdmin(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("yacht_spa_ops_session_token");
    setAdmin(null);
  };

  const setDeviceLabel = async (label) => {
    await api.put("/auth/device-label", { label });
  };

  return (
    <AuthCtx.Provider value={{ admin, loading, login, logout, setDeviceLabel }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
